import { supabase } from "@/integrations/supabase/client";

// Types for currency functionality
export interface ExchangeRate {
  id: string;
  base_currency: string;
  target_currency: string;
  rate: number;
  is_direct?: boolean;
  updated_at: string;
  created_at?: string;
}

// Define API response interface
interface ExchangeRateApiResponse {
  success: boolean;
  terms: string;
  privacy: string;
  timestamp: number;
  source: string;
  quotes: Record<string, number>;
}

// Define supported currencies
export const SUPPORTED_CURRENCIES = [
  { value: "TRY", label: "Türk Lirası", symbol: "₺" },
  { value: "USD", label: "US Dollar", symbol: "$" },
  { value: "EUR", label: "Euro", symbol: "€" },
  { value: "GBP", label: "British Pound", symbol: "£" },
  { value: "JPY", label: "Japanese Yen", symbol: "¥" },
  { value: "RUB", label: "Russian Ruble", symbol: "₽" },
  { value: "DINAR", label: "Kuwaiti Dinar", symbol: "KWD" },
  { value: "SAR", label: "Saudi Riyal", symbol: "SAR" },
  { value: "CHF", label: "Swiss Franc", symbol: "CHF" },
  { value: "XAU", label: "Gold", symbol: "XAU" },
  { value: "BTC", label: "Bitcoin", symbol: "₿" }
];

// Get currency codes for API calls (some currencies have different codes)
const getApiCode = (code: string): string => {
  switch (code) {
    case 'DINAR': return 'KWD';
    default: return code;
  }
};

// Exchange Rate API endpoint - ONLY using this API now
const EXCHANGE_API_URL = 'https://api.exchangerate.host/live';
const API_ACCESS_KEY = import.meta.env.VITE_EXCHANGE_RATE_API_KEY || '';

// Track last API call time to prevent excessive calls
let lastApiCallTime: Date | null = null;
const API_THROTTLE_MINUTES = 10; // Minimum minutes between API calls

export type CurrencyCode = typeof SUPPORTED_CURRENCIES[number]['value'];

export const currencyAPI = {
  // Get the latest exchange rates
  async getExchangeRates() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        
        throw new Error('Authentication required for exchange rate operations');
      }

      // First check if we have rates in the database from the last 24 hours
      const oneDayAgo = new Date();
      oneDayAgo.setHours(oneDayAgo.getHours() - 24); // 24 hours ago
      
      // Find the most recent timestamp where we have a complete set of rates
      const { data: recentRates, error: ratesError } = await supabase
        .from('exchange_rates')
        .select('*')
        .gte('updated_at', oneDayAgo.toISOString())
        .order('updated_at', { ascending: false });
      
      if (!ratesError && recentRates && recentRates.length > 0) {
        // Get unique timestamps
        const uniqueTimestamps = [...new Set(recentRates.map(r => r.updated_at))];
        
        if (uniqueTimestamps.length > 0) {
          // Get the most recent timestamp
          const latestTimestamp = uniqueTimestamps[0];
          
          // Filter rates for the latest timestamp
          const latestRates = recentRates.filter(r => r.updated_at === latestTimestamp);
          
          // Check if we have enough rates for all currency pairs
          const expectedPairCount = SUPPORTED_CURRENCIES.length * (SUPPORTED_CURRENCIES.length - 1);
          
          if (latestRates.length >= expectedPairCount / 2) { // Allow some flexibility in the count
            
            return { data: latestRates, error: null };
          }
        }
      }

      // If no valid recent rates, try to fetch new rates from API
      try {
        
        return await this.fetchAndUpdateRates();
      } catch (fetchError) {
        
        
        // If API fetch fails, try to get the most recent rates from the database (no time limit)
        const { data: latestTimestamp, error: latestError } = await supabase
          .from('exchange_rates')
          .select('updated_at')
          .order('updated_at', { ascending: false })
          .limit(1);
        
        if (!latestError && latestTimestamp && latestTimestamp.length > 0) {
          const timestamp = latestTimestamp[0].updated_at;
          
          const { data: latestRates, error: ratesError } = await supabase
            .from('exchange_rates')
            .select('*')
            .eq('updated_at', timestamp);
          
          if (!ratesError && latestRates && latestRates.length > 0) {
            
            return { data: latestRates, error: null };
          }
        }
        
        // If we can't get any rates from the database
        throw new Error('No exchange rates available. API fetch failed and no cached rates found.');
      }
    } catch (error) {
      
      return { data: null, error };
    }
  },

  // Fetch rates from external API and update database
  async fetchAndUpdateRates() {
    try {
      
      
      // First check if we have rates in the database from the last 24 hours
      const oneDayAgo = new Date();
      oneDayAgo.setHours(oneDayAgo.getHours() - 24); // 24 hours ago
      
      const { data: recentRates, error: ratesError } = await supabase
        .from('exchange_rates')
        .select('*')
        .gte('updated_at', oneDayAgo.toISOString())
        .order('updated_at', { ascending: false });
      
      if (!ratesError && recentRates && recentRates.length > 0) {
        // Get unique timestamps
        const uniqueTimestamps = [...new Set(recentRates.map(r => r.updated_at))];
        
        if (uniqueTimestamps.length > 0) {
          // Get the most recent timestamp
          const latestTimestamp = uniqueTimestamps[0];
          
          // Filter rates for the latest timestamp
          const latestRates = recentRates.filter(r => r.updated_at === latestTimestamp);
          
          // Check if we have enough rates for all currency pairs
          const expectedPairCount = SUPPORTED_CURRENCIES.length * (SUPPORTED_CURRENCIES.length - 1);
          
          if (latestRates.length >= expectedPairCount / 2) { // Allow some flexibility in the count
            
            return { data: latestRates, error: null };
          }
        }
      }
      
      
      
      // Get exchange rates from the exchangerate.host API
      const exchangeRates = await this.fetchFromExchangeRateAPI();
      
      if (!exchangeRates || Object.keys(exchangeRates).length === 0) {
        throw new Error('Failed to fetch exchange rates from API');
      }
      
      // Create a map of all rates (including calculated cross-rates)
      const allRates = this.calculateAllRates(exchangeRates);
      
      // Now proceed with database operations
      const now = new Date().toISOString();
      const dbRates: {
        base_currency: string;
        target_currency: string;
        rate: number;
        is_direct?: boolean;
        updated_at: string;
      }[] = [];
      
      // Convert the rates map to an array of rate objects for the database
      for (const [pair, rate] of Object.entries(allRates)) {
        const [fromCurrency, toCurrency] = pair.split('-');
        // Mark as direct if from USD, otherwise calculated
        const isDirect = fromCurrency === 'USD';
        
        dbRates.push({
          base_currency: fromCurrency,
          target_currency: toCurrency,
          rate: Number(rate),
          is_direct: isDirect,
          updated_at: now
        });
      }
      
      if (dbRates.length === 0) {
        throw new Error('No valid rates found in API response');
      }
      
      
      
      // Insert all rates to the database as new records
      try {
        // Use upsert instead of insert to avoid duplicate key violations
        const { data: insertedRates, error } = await supabase
          .from('exchange_rates')
          .upsert(dbRates, { 
            onConflict: 'base_currency,target_currency',
            ignoreDuplicates: false
          })
          .select();
        
        if (error) {
          
          // Create a response in the expected format even if storage failed
          return { 
            data: dbRates.map((rate, index) => ({ 
              id: String(index+1), 
              ...rate 
            })) as ExchangeRate[], 
            error: null 
          };
        }
        
        // Keep only the last 30 days of rates to prevent database from growing too large
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        await supabase
          .from('exchange_rates')
          .delete()
          .lt('updated_at', thirtyDaysAgo.toISOString());
        
        
        return { data: insertedRates, error: null };
      } catch (dbError) {
        
        return { 
          data: dbRates.map((rate, index) => ({ 
            id: String(index+1), 
            ...rate 
          })) as ExchangeRate[], 
          error: dbError 
        };
      }
    } catch (error) {
      
      throw error;
    }
  },

  // Fetch from exchangerate.host live API
  async fetchFromExchangeRateAPI() {
    if (!API_ACCESS_KEY) {
      throw new Error('Exchange Rate API key is not configured in environment variables');
    }
    
    // Check if we made an API call recently
    const now = new Date();
    if (lastApiCallTime) {
      // Calculate minutes since last API call
      const minutesSinceLastCall = (now.getTime() - lastApiCallTime.getTime()) / (1000 * 60);
      
      if (minutesSinceLastCall < API_THROTTLE_MINUTES) {
        
        throw new Error(`API call throttled - wait at least ${API_THROTTLE_MINUTES} minutes between calls`);
      }
    }
    
    // Update last API call time
    lastApiCallTime = now;
    
    
    
    // Build the API URL
    const apiUrl = `${EXCHANGE_API_URL}?access_key=${API_ACCESS_KEY}&format=1`;
    
    // Add timeout for the fetch request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
    
    try {
      // Fetch the exchange rates
      
      const response = await fetch(apiUrl, { 
        signal: controller.signal,
        headers: { 'Accept': 'application/json' }
      });
      
      clearTimeout(timeoutId);
      
      
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status} - ${response.statusText}`);
      }
      
      // Parse response JSON
      const data = await response.json() as ExchangeRateApiResponse;
      
      
      if (!data.success || !data.quotes) {
        
        throw new Error(`Invalid API response format: ${JSON.stringify(data)}`);
      }
      
      return data.quotes;
    } catch (error) {
      
      throw error;
    }
  },

  // Calculate all possible currency pairs from USD-based rates
  calculateAllRates(quotes: Record<string, number>) {
    const allRates: Record<string, number> = {};
    
    // Step 1: Extract only the needed USD-based rates from the API response
    const usdRates: Record<string, number> = {};
    const requiredCurrencies = ["USD", "EUR", "GBP", "JPY", "RUB", "KWD", "SAR", "CHF", "XAU", "BTC", "TRY"];
    
    // Map our internal currency codes to API codes
    const currencyToApiCode: Record<string, string> = {
      'DINAR': 'KWD'
    };
    
    // For each quote, extract only the currencies we need
    for (const [quotePair, rate] of Object.entries(quotes)) {
      // Quote pairs from API are in format "USDXXX" where XXX is the currency code
      if (quotePair.startsWith('USD')) {
        const currencyCode = quotePair.substring(3); // Remove "USD" prefix
        
        // Check if this is one of our required currencies
        if (requiredCurrencies.includes(currencyCode)) {
          usdRates[currencyCode] = Number(rate); // Store as USD to Currency rate
          
        }
      }
    }
    
    // Make sure we have USD to USD rate (which is 1)
    usdRates['USD'] = 1;
    
    // Check if we have all required currencies
    
    
    // Step 2: Add direct USD-based rates to our result
    for (const [currency, rate] of Object.entries(usdRates)) {
      // USD to currency rate
      allRates[`USD-${currency}`] = rate;
      
      // Currency to USD rate (inverse)
      if (rate !== 0) {
        allRates[`${currency}-USD`] = 1 / rate;
        
      }
    }
    
    // Step 3: Calculate cross-rates for all other currency pairs
    for (const fromCurrency of Object.keys(usdRates)) {
      for (const toCurrency of Object.keys(usdRates)) {
        if (fromCurrency === toCurrency) continue;
        
        // Skip if we already have this pair
        if (allRates[`${fromCurrency}-${toCurrency}`]) continue;
        
        // Calculate cross rate: fromCurrency → USD → toCurrency
        const fromToUsd = allRates[`${fromCurrency}-USD`];
        const usdToTo = allRates[`USD-${toCurrency}`];
        
        if (fromToUsd && usdToTo) {
          // Calculate the cross rate
          const crossRate = fromToUsd * usdToTo;
          allRates[`${fromCurrency}-${toCurrency}`] = crossRate;
          
        }
      }
    }
    
    // Step 4: Map API currency codes to our internal codes
    const mappedRates: Record<string, number> = {};
    
    // Create a map to convert between API codes and our internal codes
    const codeMap: Record<string, string> = {
      'KWD': 'DINAR'
    };
    
    // Map all rates using our internal currency codes
    for (const [pair, rate] of Object.entries(allRates)) {
      const [fromCode, toCode] = pair.split('-');
      
      const mappedFromCode = codeMap[fromCode] || fromCode;
      const mappedToCode = codeMap[toCode] || toCode;
      
      mappedRates[`${mappedFromCode}-${mappedToCode}`] = rate;
    }
    
    return mappedRates;
  },

  // Convert amount between currencies
  async convertCurrency(amount: number, fromCurrency: CurrencyCode, toCurrency: CurrencyCode) {
    
    
    if (fromCurrency === toCurrency) {
      
      return { data: amount, error: null };
    }

    try {
      // Get the latest exchange rates
      
      const { data: rates, error: ratesError } = await this.getExchangeRates();
      
      if (ratesError || !rates) {
        
        throw ratesError || new Error('Failed to fetch exchange rates');
      }
      
      
      
      // Find the appropriate rate
      const rateObj = rates.find(r => 
        r.base_currency === fromCurrency && r.target_currency === toCurrency
      );
      
      if (rateObj && rateObj.rate) {
        // Calculate the converted amount
        const convertedAmount = amount * rateObj.rate;
        
        return { data: convertedAmount, error: null };
      }
      
      // If direct rate not found, try to calculate it using USD as intermediate
      
      const fromToUsd = rates.find(r => 
        r.base_currency === fromCurrency && r.target_currency === 'USD'
      );
      
      const usdToTarget = rates.find(r => 
        r.base_currency === 'USD' && r.target_currency === toCurrency
      );
      
      
      
      if (fromToUsd && fromToUsd.rate && usdToTarget && usdToTarget.rate) {
        // Calculate the cross rate: fromCurrency → USD → toCurrency
        const crossRate = fromToUsd.rate * usdToTarget.rate;
        const convertedAmount = amount * crossRate;
        
        return { data: convertedAmount, error: null };
      }
      
      
      throw new Error(`Exchange rate not found for ${fromCurrency} to ${toCurrency}`);
    } catch (error) {
      
      return { 
        data: null, 
        error: error instanceof Error 
          ? error 
          : new Error(`Failed to convert ${fromCurrency} to ${toCurrency}`) 
      };
    }
  },

  // Get historical exchange rate data for analysis
  async getHistoricalRates(days: number = 30) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Authentication required for exchange rate operations');
      }
      
      // Get rates from the last X days
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - days);
      
      // First, get all unique timestamps from the period
      const { data: timestamps, error: timestampError } = await supabase
        .from('exchange_rates')
        .select('updated_at')
        .gte('updated_at', daysAgo.toISOString())
        .order('updated_at', { ascending: true });
      
      if (timestampError) {
        throw timestampError;
      }
      
      if (!timestamps || timestamps.length === 0) {
        return { data: [], error: new Error('No historical data available for the requested period') };
      }
      
      // Get unique timestamps (one per batch)
      const uniqueTimestamps = [...new Set(timestamps.map(t => t.updated_at))];
      
      // Structure to hold historical data by date
      const historicalData: Record<string, ExchangeRate[]> = {};
      
      // Process each timestamp in smaller batches to avoid DB limits
      const batchSize = 10;
      for (let i = 0; i < uniqueTimestamps.length; i += batchSize) {
        const batchTimestamps = uniqueTimestamps.slice(i, i + batchSize);
        
        // Get rates for each timestamp
        const { data: batchRates, error: batchError } = await supabase
          .from('exchange_rates')
          .select('*')
          .in('updated_at', batchTimestamps);
        
        if (batchError) {
          
          continue;
        }
        
        if (batchRates && batchRates.length > 0) {
          // Group rates by timestamp
          batchRates.forEach(rate => {
            const dateStr = new Date(rate.updated_at).toISOString().split('T')[0];
            if (!historicalData[dateStr]) {
              historicalData[dateStr] = [];
            }
            historicalData[dateStr].push(rate);
          });
        }
      }
      
      return { data: historicalData, error: null };
    } catch (error) {
      
      return { data: null, error };
    }
  },

  // Force refresh of exchange rates, bypassing cache
  async forceRefreshRates() {
    
    try {
      // Fetch the rates
      
      try {
        const result = await this.fetchAndUpdateRates();
        
        // Log the result
        
        if (result.data) {
          
          
          // Count rates by base currency
          const baseCurrencyCounts: Record<string, number> = {};
          result.data.forEach(rate => {
            const base = rate.base_currency;
            baseCurrencyCounts[base] = (baseCurrencyCounts[base] || 0) + 1;
          });
          
          
          
          // Count direct vs calculated rates
          const directRates = result.data.filter(r => r.is_direct).length;
          const calculatedRates = result.data.filter(r => !r.is_direct).length;
          
        }
        if (result.error) {
          
        }
        
        // Summarize the refresh result
        
        
        return result;
      } catch (fetchError) {
        
        throw fetchError;
      }
    } catch (error) {
      
      
      // Try to get most recent rates from database as fallback
      
      const { data: recentRates, error: dbError } = await supabase
        .from('exchange_rates')
        .select('updated_at')
        .order('updated_at', { ascending: false })
        .limit(1);
      
      
      
      if (!dbError && recentRates && recentRates.length > 0) {
        const latestTimestamp = recentRates[0].updated_at;
        
        
        const { data: allRecentRates, error: allRecentError } = await supabase
          .from('exchange_rates')
          .select('*')
          .eq('updated_at', latestTimestamp);
        
        
        
        if (!allRecentError && allRecentRates && allRecentRates.length > 0) {
          
          return { data: allRecentRates, error: null };
        }
      }
      
      // If no rates found in the database
      
      return { data: null, error: new Error('Failed to refresh rates and no previous rates found') };
    }
  }
};